import {useEffect, useMemo, useState} from "react";
import {t} from "i18next";
import {s} from "@/app/utils/s";
import {format, parseISO} from 'date-fns';
import {OpenMatchModelResponse} from "@/app/api/open_match";
import {route} from "@/app/routes/AppRoutes";
import {NavigateFunction} from "react-router-dom";

export function debounce<T extends (...args: any[]) => void>(
  func: T,
  delay: number
): T & { cancel: () => void } {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const debounced = (...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };

  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debounced as T & { cancel: () => void };
}

export const isValidEmail = (email: string): boolean => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(query);
    setMatches(mq.matches);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

export const passwordGetRules = (password: string): string[] => {
  const rules: string[] = [];
  if (password.length < 8) rules.push(t(s.atLeast8Chars));
  if (!/[A-Z]/.test(password)) rules.push(t(s.atLeast1Uppercase));
  if (!/[a-z]/.test(password)) rules.push(t(s.atLeast1Lowercase));
  if (!/[0-9]/.test(password)) rules.push(t(s.atLeast1Number));
  return rules;
}

export const useMessageTime = (isoString?: string): string => {
  return useMemo(() => {
    if (!isoString) return '';
    
    // Check if the ISO string has timezone information
    const hasTimezone = isoString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(isoString);
    
    let date;
    if (hasTimezone) {
      // If the ISO string has timezone information, parseISO will handle it correctly
      date = parseISO(isoString);
    } else {
      // If the ISO string doesn't have timezone information, assume it's in UTC
      // and append 'Z' to indicate UTC time
      date = parseISO(isoString + 'Z');
    }
    
    if (isNaN(date.getTime())) return '';
    
    // Format the date in the client's local timezone
    return format(date, 'HH:mm');
  }, [isoString]);
};

export const isNumeric = (val?: string | number): boolean => {
  if (!val) return false;
  return !isNaN(Number(val.toString()));
}

export function getDaysAgo(dateString: string): string {
  const createdDate = new Date(dateString);
  const now = new Date();

  const diffMs = now.getTime() - createdDate.getTime();

  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 1) {
    return t(s.today);
  }

  return t(s.dAgo, {days: diffDays});
}

export function removeUndefined<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== undefined && value !== null)
  ) as Partial<T>;
}

export function normalizeCoords(coords: { lat: number; lng?: number; lon?: number }): { lat: number; lng: number } {
  return {
    lat: coords.lat,
    lng: coords.lng ?? coords.lon ?? 0
  };
}

export function toSnakeUpperCase(value: string): string {
  return value
    .replace(/([A-Z])/g, '_$1')
    .toUpperCase();
}

export function fromEnumKeyToEnumValue<T>(enumObj: T, key: string): T[keyof T] | undefined {
  if (Object.prototype.hasOwnProperty.call(enumObj, key)) {
    const value = enumObj[key as keyof T];
    if (typeof value === 'string') {
      return value as T[keyof T];
    }
  }
  return undefined;
}

export function handleMatchClick(match: OpenMatchModelResponse, navigate: NavigateFunction): void {
  // Create query parameters with match details
  const params = new URLSearchParams();
  params.append('matchId', match.id?.toString() || '');
  
  // Add only date parameter to narrow down the search
  if (match.eventDate) {
    // Format date as YYYY-MM-DD to avoid LocalDate conversion issues on the server
    const date = new Date(match.eventDate);
    const formattedDate = date.toISOString().split('T')[0]; // Get only the date part (YYYY-MM-DD)
    params.append('eventDate', formattedDate);
  }
  
  // Navigate to match detail page with query parameters
  navigate(`${route.matchDetailPage}?${params.toString()}`);
}